import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CreditCard, Settings, HelpCircle, LogOut, ChevronRight, Edit2, BarChart3, Clock, Mic, Share2, Bell, Sparkles, Database, Wrench } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface ProfileScreenProps {
  onNavigate: (screen: string) => void;
  onLogout: () => void;
  isActivated?: boolean;
}
export function ProfileScreen({
  onNavigate,
  onLogout,
  isActivated: activatedProp = false
}: ProfileScreenProps) {
  const {
    isLight
  } = useTheme();
  const [isActivated, setIsActivated] = useState(activatedProp);
  const [hasData, setHasData] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  useEffect(() => {
    setIsActivated(activatedProp);
  }, [activatedProp]);
  const menuItems = [{
    icon: Mic,
    label: 'Caller Experience',
    screen: 'callerExperience'
  }, {
    icon: BarChart3,
    label: 'Usage',
    screen: 'usage'
  }, {
    icon: Bell,
    label: 'Notifications',
    screen: 'notifications'
  }, {
    icon: Settings,
    label: 'Settings',
    screen: 'settings'
  }, {
    icon: Share2,
    label: 'Share App',
    action: () => {
      if (navigator.share) {
        navigator.share({
          title: 'Vexu.ai',
          text: 'Check out Vexu.ai — your AI answering machine!',
          url: 'https://vexu.ai'
        });
      }
    }
  }, {
    icon: HelpCircle,
    label: 'Help & Support',
    screen: 'support'
  }];
  return <div className="h-full flex flex-col p-6 pb-24 overflow-y-auto relative" data-id="element-2154">
      {/* Background orb */}
      <div className={`absolute top-10 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-2155" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6 relative z-10" data-id="element-2156">
        <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2157">
          Account
        </h1>
        <button onClick={() => setShowDevTools(!showDevTools)} className={`p-2 rounded-full transition-colors ${showDevTools ? isLight ? 'bg-slate-200 text-slate-700' : 'bg-white/15 text-white' : isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2158">
          <Wrench className="w-4 h-4" data-id="element-2159" />
        </button>
      </div>

      {/* Designer Controls (collapsible) */}
      <AnimatePresence data-id="element-2160">
        {showDevTools && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden mb-4 relative z-10" data-id="element-2161">
            <div className="flex items-center gap-2 pb-2" data-id="element-2162">
              <button onClick={() => setIsActivated(!isActivated)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${isActivated ? isLight ? 'bg-vexu/10 border-vexu/20 text-vexu-700' : 'bg-vexu/20 border-vexu/30 text-vexu' : isLight ? 'bg-slate-100 border-slate-200 text-slate-600' : 'bg-white/10 border-white/10 text-zinc-300'}`} data-id="element-2163">
                <Sparkles className="w-3 h-3" data-id="element-2164" />
                {isActivated ? 'Activated' : 'Not Activated'}
              </button>
              <button onClick={() => setHasData(!hasData)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${hasData ? isLight ? 'bg-slate-800 border-slate-900 text-white' : 'bg-white border-white text-black' : isLight ? 'bg-white border-slate-200 text-slate-700 shadow-sm' : 'bg-dark-card border-white/10 text-zinc-300'}`} data-id="element-2165">
                <Database className="w-3 h-3" data-id="element-2166" />
                {hasData ? 'Clear Data' : 'Simulate Data'}
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="space-y-5 relative z-10" data-id="element-2167">
        {/* Avatar Card */}
        <div className={`flex flex-col items-center backdrop-blur-xl border rounded-2xl p-5 shadow-lg transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-slate-200/50' : 'bg-dark-card/60 border-white/10 shadow-black/50'}`} data-id="element-2168">
          <div className="relative mb-3" data-id="element-2169">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold border transition-colors ${isLight ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-white/5 text-zinc-400 border-white/10'}`} data-id="element-2170">
              AJ
            </div>
            <button className={`absolute bottom-0 right-0 p-1.5 rounded-full shadow-lg border-2 transition-colors ${isLight ? 'bg-vexu text-white shadow-vexu/20 border-white' : 'bg-vexu text-black shadow-vexu/20 border-dark'}`} data-id="element-2171">
              <Edit2 className="w-3 h-3" data-id="element-2172" />
            </button>
          </div>

          <h2 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2173">
            Alex Johnson
          </h2>

          <div className="flex items-center gap-2 mt-1 mb-3" data-id="element-2174">
            <Phone className={`w-3.5 h-3.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2175" />
            <span className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2176">
              +1 (555) 123-4567
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-2177">
              Primary
            </span>
          </div>

          {isActivated ? <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2178">
              <div className={`w-2 h-2 rounded-full ${isLight ? 'bg-vexu shadow-[0_0_4px_rgba(0,200,83,0.5)]' : 'bg-vexu shadow-[0_0_8px_rgba(0,200,83,0.8)]'}`} data-id="element-2179" />
              <span className={`text-sm font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2180">
                Active
              </span>
            </div> : <button onClick={() => onNavigate('planSelection')} className="px-5 py-2 rounded-full text-sm font-bold bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-2181">
              Activate Vexu
            </button>}
        </div>

        {/* Compact Plan & Usage Card */}
        {isActivated && hasData && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`backdrop-blur-xl border rounded-2xl p-4 shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2182">
            <div className="flex items-center justify-between mb-3" data-id="element-2183">
              <div className="flex items-center gap-2.5" data-id="element-2184">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLight ? 'bg-purple-50 text-purple-600' : 'bg-purple-500/10 text-purple-400'}`} data-id="element-2185">
                  <CreditCard className="w-4 h-4" data-id="element-2186" />
                </div>
                <div data-id="element-2187">
                  <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2188">
                    Pro Plan
                  </span>
                  <span className={`text-[10px] font-medium ml-2 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2189">
                    Active
                  </span>
                </div>
              </div>
              <button onClick={() => onNavigate('planSelection')} className={`text-xs font-medium ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-white'}`} data-id="element-2190">
                Manage
              </button>
            </div>
            <div className="flex items-center justify-between mb-1.5" data-id="element-2191">
              <div className="flex items-center gap-1.5" data-id="element-2192">
                <Clock className={`w-3 h-3 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2193" />
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2194">
                  Minutes Used
                </span>
              </div>
              <span className={`text-[11px] font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2195">
                67 / 100 min
              </span>
            </div>
            <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/10'}`} data-id="element-2196">
              <div className="h-full rounded-full bg-vexu transition-all duration-500" style={{
            width: '67%'
          }} data-id="element-2197" />
            </div>
          </motion.div>}

        {/* Menu List */}
        <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2198">
          {menuItems.map((item, i) => {
          const Icon = item.icon;
          return <button key={i} onClick={() => 'action' in item && item.action ? item.action() : onNavigate(item.screen!)} className={`w-full p-4 flex items-center justify-between transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'} ${i < menuItems.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-2199">
                <div className="flex items-center gap-3" data-id="element-2200">
                  <Icon className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-2201" />
                  <span className={`font-medium text-sm ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2202">
                    {item.label}
                  </span>
                </div>
                <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2203" />
              </button>;
        })}
        </div>

        {/* Sign Out */}
        <button onClick={onLogout} className={`w-full p-4 flex items-center justify-center gap-2 font-bold text-sm rounded-2xl transition-colors backdrop-blur-lg border ${isLight ? 'text-red-600 bg-white border-red-100 hover:bg-red-50 shadow-sm' : 'text-red-400 bg-white/5 border-white/10 hover:bg-red-500/10'}`} data-id="element-2204">
          <LogOut className="w-4 h-4" data-id="element-2205" />
          Sign Out
        </button>
      </div>
    </div>;
}